import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  Calendar, 
  ClipboardList, 
  Briefcase, 
  Settings, 
  ChevronDown, 
  ChevronRight, 
  LogOut, 
  Building2,
  Menu,
  X
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  hasSubmenu?: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
  children?: React.ReactNode;
  onClick?: () => void;
}

const NavItem = ({
  to,
  icon,
  label,
  hasSubmenu,
  isOpen,
  onToggle,
  children,
  onClick,
}: NavItemProps) => {
  const location = useLocation();
  const isActive = location.pathname === to || location.pathname.startsWith(to + '/');

  if (hasSubmenu) {
    return (
      <div>
        <button
          onClick={onToggle}
          className={cn(
            'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all',
            'text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground',
            isActive && 'bg-sidebar-accent text-sidebar-primary'
          )}
        >
          {icon}
          <span className="flex-1 text-left">{label}</span>
          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        {isOpen && <div className="ml-6 mt-1 space-y-1">{children}</div>}
      </div>
    );
  }

  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all',
          'text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground',
          isActive && 'bg-sidebar-accent text-sidebar-primary'
        )
      }
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  );
};

export const Sidebar = () => {
  const { logout, user } = useAuth();
  const [recruitmentOpen, setRecruitmentOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      {/* Mobile Menu Button */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed left-4 top-4 z-50 lg:hidden"
        onClick={() => setMobileOpen(!mobileOpen)}
      >
        {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </Button>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={closeMobile}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-40 flex h-screen w-64 flex-col bg-sidebar border-r border-sidebar-border transition-transform duration-300',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        {/* Logo */}
        <div className="flex h-16 items-center gap-3 border-b border-sidebar-border px-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary">
            <Building2 className="h-6 w-6 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-sidebar-foreground">HirYou</h1>
            <p className="text-xs text-sidebar-muted">Company Portal</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
          <NavItem
            to="/"
            icon={<LayoutDashboard className="h-5 w-5" />}
            label="Dashboard"
            onClick={closeMobile}
          />
          <NavItem
            to="/candidates"
            icon={<Users className="h-5 w-5" />}
            label="Candidates"
            onClick={closeMobile}
          />
          <NavItem
            to="/recruitment"
            icon={<Briefcase className="h-5 w-5" />}
            label="Recruitment Status"
            hasSubmenu
            isOpen={recruitmentOpen}
            onToggle={() => setRecruitmentOpen(!recruitmentOpen)}
          >
            <NavItem
              to="/recruitment/shortlisted"
              icon={<span className="h-2 w-2 rounded-full bg-primary" />}
              label="Shortlisted"
              onClick={closeMobile}
            />
            <NavItem
              to="/recruitment/interviews"
              icon={<span className="h-2 w-2 rounded-full bg-accent" />}
              label="Interviews"
              onClick={closeMobile}
            />
            <NavItem
              to="/recruitment/hired"
              icon={<span className="h-2 w-2 rounded-full bg-success" />}
              label="Hired"
              onClick={closeMobile}
            />
          </NavItem>
          <NavItem
            to="/interviews"
            icon={<Calendar className="h-5 w-5" />}
            label="Schedule Interviews"
            onClick={closeMobile}
          />
          <NavItem
            to="/tasks"
            icon={<ClipboardList className="h-5 w-5" />}
            label="Assign Tasks"
            onClick={closeMobile}
          />
          <NavItem
            to="/settings"
            icon={<Settings className="h-5 w-5" />}
            label="Settings"
            onClick={closeMobile}
          />
        </nav>

        {/* User Info & Footer */}
        <div className="border-t border-sidebar-border p-4">
          <div className="mb-3 flex items-center gap-3 px-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground font-semibold">
              {user?.name?.charAt(0).toUpperCase() || 'T'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name || 'TechCorp Inc.'}</p>
              <p className="text-xs text-sidebar-muted truncate">{user?.email || 'Admin'}</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-sidebar-foreground/80 transition-all hover:bg-sidebar-accent hover:text-sidebar-foreground"
          >
            <LogOut className="h-5 w-5" />
            <span>Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
};
